import { Button, Typography } from 'antd'
import type { ReactNode } from 'react'
import { MdOutlinePhotoLibrary } from 'react-icons/md'

import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'

import { DELAY_SOURCE_MAP, delayStatusOption, type DelaySource } from '../../Contracts/DelayStatus'

import styles from './DelaysList.module.css'

const { Text } = Typography

/* eslint-disable no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures */
interface DelaysListProps {
    delays: any[]
    onShowPhotos: (urls: string[]) => void
    onResolve: (delay: any) => void
    /** Показувати «Вирішити» для відкритих затримок (_permissions.resolve_delay) */
    canResolve?: boolean
    /** Підпис над статусом — напр. завдання, коли список збирає затримки кількох завдань (заявка) */
    itemLabel?: ((delay: any) => ReactNode) | undefined
}

const CLOSED_STATUSES = ['resolved', 'not_resolved']

const formatDateTime = (value: unknown): string | null => {
    if (typeof value !== 'string' || value === '') return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const receivedTime = (delay: any): number => {
    const time = new Date(delay?.receivedAt ?? '').getTime()
    return Number.isNaN(time) ? 0 : time
}

/**
 * Затримки задачі стрічкою карток (новіші зверху) замість широкої таблиці: статус,
 * дата · джерело і дія в рядку заголовка, коментар водія з переносом рядків, фото,
 * рішення диспетчера. Вміщується в картку 960px без горизонтальної прокрутки.
 */
export const DelaysList = ({ delays, onShowPhotos, onResolve, canResolve = false, itemLabel }: DelaysListProps) => {
    if (delays.length === 0) {
        return <Text type="secondary">Затримок немає</Text>
    }

    const sorted = [...delays].sort((a, b) => receivedTime(b) - receivedTime(a))

    return (
        <ul className={cx(styles, 'list')}>
            {sorted.map((delay: any, index: number) => {
                const isClosed = CLOSED_STATUSES.includes(delay.status)
                const received = formatDateTime(delay.receivedAt)
                const resolved = formatDateTime(delay.resolvedAt)
                const source = DELAY_SOURCE_MAP[delay.source as DelaySource]?.label ?? delay.source
                // Затримку надіслано з PWA без зв'язку: показуємо, коли водій її фактично створив
                const offline = formatDateTime(delay.offlineAt)
                const meta = [received, source, offline && `офлайн, створено ${offline}`].filter(Boolean).join(' · ')
                const photos: string[] = Array.isArray(delay.photoUrls) ? delay.photoUrls : []

                return (
                    <li
                        key={delay.uuid || index}
                        className={`${cx(styles, 'item')}${isClosed ? '' : ` ${cx(styles, 'itemOpen')}`}`}
                    >
                        {itemLabel && <div className={cx(styles, 'label')}>{itemLabel(delay)}</div>}
                        <div className={cx(styles, 'head')}>
                            <StatusBadge option={delayStatusOption(delay.status)} fallbackLabel={delay.status} />
                            {meta && <span className={cx(styles, 'meta')}>{meta}</span>}
                            {!isClosed && canResolve && (
                                <Button size="small" className={cx(styles, 'action')} onClick={() => onResolve(delay)}>
                                    Вирішити
                                </Button>
                            )}
                        </div>

                        <p className={cx(styles, 'text')}>
                            {delay.driverComment || <span className={cx(styles, 'empty')}>Без коментаря</span>}
                        </p>

                        {photos.length > 0 && (
                            <Button
                                size="small"
                                type="text"
                                icon={<MdOutlinePhotoLibrary />}
                                className={cx(styles, 'photos')}
                                onClick={() => onShowPhotos(photos)}
                            >
                                Фото ({photos.length})
                            </Button>
                        )}

                        {(delay.resolverComment || (isClosed && resolved)) && (
                            <div className={cx(styles, 'resolution')}>
                                <span className={cx(styles, 'resolutionLabel')}>
                                    Рішення{resolved ? ` · ${resolved}` : ''}
                                </span>
                                {delay.resolverComment && (
                                    <p className={cx(styles, 'resolutionText')}>{delay.resolverComment}</p>
                                )}
                            </div>
                        )}
                    </li>
                )
            })}
        </ul>
    )
}
